import { useEffect, useState } from "react";
import { create } from "zustand";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, SearchField, Select, Table } from "@studio";
import { PanelError } from "../console/ui";
import { SeoNotice } from "@studio-features/meta-crew/seo-shared";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { fmtDate, fmtDateTime, num } from "../lib/format";
import { useAction, useAdminSeoKeywords } from "../lib/queries";
import { deltaText, rankDelta, rankText, type SeoResult, useAdminSeoResults } from "../lib/seo-api";
import type { Me, SeoKeyword } from "../lib/types";
import { Empty, ErrorBox, InfoRow, Loading, Panel, StatGrid, Tabs } from "../console/ui";
import { SeoAssetsPanel, SeoConnections } from "./seo-ops";

/** 직원 키워드 목록 행: 서버가 마지막 수집 시각·실패 사유를 함께 준다. */
type SeoKeywordRow = SeoKeyword & { lastCollectedAt?: string | null; lastCollectError?: string | null };

/**
 * 다른 화면(모니터링 › SEO 수집 등)에서 고객사의 SEO 탭을 열 때. 시안 06a1acb: SEO 관리는 고객사 상세 › SEO 탭 안에 있으므로
 * 관리자 화면이 이 값을 읽어 고객사 상세 다이얼로그를 연다.
 */
export const useSeoFocus = create<{ customerId: string | null; tab: string | null; focus: (customerId: string, tab?: string) => void; clear: () => void }>((set) => ({
  customerId: null,
  tab: null,
  focus: (customerId, tab = "연결") => set({ customerId, tab }),
  clear: () => set({ customerId: null, tab: null }),
}));

/** 키워드 행에 붙일 최신 결과: 영역별 최고 순위 중 가장 좋은 것과 마지막 수집 시각. */
function bestOf(results: SeoResult[]): { best: SeoResult | null; collectedAt: string | null } {
  if (!results.length) return { best: null, collectedAt: null };
  const found = results.filter((r) => r.found && r.bestRank !== null).sort((a, b) => a.bestRank! - b.bestRank!);
  const collectedAt = results.map((r) => r.collectedAt).sort().pop() ?? null;
  return { best: found[0] ?? results[0]!, collectedAt };
}

/** 시안 seo-data 와 같은 표기. 서버 값(tracking/stopped/ended, blog/cafe…)을 화면 문구로. */
const statusLabel: Record<SeoKeyword["status"], [string, string]> = {
  tracking: ["추적 중", "teal"],
  stopped: ["중지", "slate"],
  ended: ["종료", "slate"],
};
const areaLabel: Record<string, string> = { blog: "블로그", cafe: "카페", news: "뉴스", web: "웹문서", local: "지역", shop: "쇼핑" };
const areasText = (a: string[]) => a.map((x) => areaLabel[x] ?? x).join(" · ");

/** 직원: 고객사 상세 › SEO 탭 (시안 SeoManagement, 06a1acb 부터 고객사 재선택 없음). 키워드·자산·연결. */
export function SeoManagementScreen({ me, customerId, initialTab = "키워드" }: { me: Me; customerId: string; initialTab?: string }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => setTab(initialTab), [initialTab, customerId]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [extend, setExtend] = useState<SeoKeyword | null>(null);
  const [remove, setRemove] = useState<SeoKeyword | null>(null);
  const q = useAdminSeoKeywords({ customerId: customerId || undefined, status: status || undefined, q: query || undefined });
  const results = useAdminSeoResults(customerId);
  const byKeyword = new Map<string, SeoResult[]>();
  for (const r of results.data?.items ?? []) byKeyword.set(r.keywordId, [...(byKeyword.get(r.keywordId) ?? []), r]);
  const canWrite = can(me, "MARKETING_ASSET_WRITE");
  const inv = [["seo"]];
  const patch = useAction((v: { id: string; body: Record<string, unknown> }) => api.patch(`/admin/seo/keywords/${v.id}`, v.body), inv);
  const del = useAction((id: string) => api.del(`/admin/seo/keywords/${id}`), inv);
  const rows = q.data?.items ?? [];
  const run = (p: Promise<unknown>, done: string) => p.then(() => setMsg(done)).catch((e) => setMsg(errorMessage(e)));
  const now = Date.now();
  return (
    <div className="seo-stack">
      <Tabs items={["키워드", "자산", "연결"]} value={tab} onChange={setTab} />
      <span className="seo-caption">고객사 상세 › SEO · {canWrite ? "관리자 전용 편집" : "조회 전용"}</span>
      {msg && <InfoRow title={msg} />}
      {tab === "키워드" ? (
        <>
          <div className="seo-notice">
            <span>
              게시 작업에 키워드를 입력하면 즉시 추적합니다. 같은 고객사 안의 중복 키워드는 합치고 요청 작업은 모두 보관합니다. 추적은 마지막 요청부터
              정책 설정의 기간(기본 90일) 동안 유지됩니다.
            </span>
          </div>
          <StatGrid
            links={{ "추적 중": { onOpen: () => setStatus("tracking"), label: "추적 중인 키워드만 보기" } }}
            items={[
              ["추적 중", rows.filter((k) => k.status === "tracking").length, "이 고객사", "teal"],
              ["이번 달 작업", rows.filter((k) => k.lastRequestedAt && new Date(k.lastRequestedAt).getMonth() === new Date().getMonth()).length, "이번 달 요청된 키워드"],
              ["확인 필요", rows.filter((k) => k.status === "tracking" && new Date(k.trackingUntil).getTime() - now < 7 * 86400_000).length, "7일 안에 추적 종료", "amber"],
            ]}
          />
          <Panel title="키워드 추적 목록">
            <FilterBar>
              <SearchField label="키워드 검색" showLabel placeholder="키워드 입력" value={query} onChange={(e) => setQuery(e.target.value)} />
              <Select label="추적 상태" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">전체</option>
                <option value="tracking">추적 중</option>
                <option value="stopped">중지</option>
                <option value="ended">종료</option>
              </Select>
            </FilterBar>
            {q.isLoading ? (
              <Loading />
            ) : q.error ? (
              <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
            ) : rows.length === 0 ? (
              <Empty>추적 중인 키워드가 없습니다. 게시 작업을 등록할 때 SEO 키워드를 넣으면 여기에 쌓입니다.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>키워드 / 요청 출처</th>
                    <th>요청일 / 종료일</th>
                    <th>검색 영역</th>
                    <th>최신 최고 순위 / 변동</th>
                    <th>마지막 수집 / 사유</th>
                    <th>상태</th>
                    {canWrite && <th>관리</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((k) => (
                    <tr key={k.id}>
                      <td>
                        <strong>{k.keyword}</strong>
                        <small className="meta-crew-sub">
                          요청 작업 {k.requestCount ?? 0}건{k.lastJobName ? ` · 최근 ${k.lastJobName}` : ""}
                        </small>
                      </td>
                      <td>
                        최초 {fmtDate(k.firstRequestedAt)}
                        <br />
                        최근 {fmtDate(k.lastRequestedAt)}
                        <br />
                        <strong>종료 {fmtDate(k.trackingUntil)}</strong>
                      </td>
                      <td>{areasText(k.areas)}</td>
                      {(() => {
                        const rs = byKeyword.get(k.id) ?? [];
                        const { best, collectedAt } = bestOf(rs);
                        const err = (k as SeoKeywordRow).lastCollectError;
                        const last = (k as SeoKeywordRow).lastCollectedAt ?? collectedAt;
                        return (
                          <>
                            <td>
                              {best ? rankText(best) : <span className="meta-crew-muted">첫 수집 대기</span>}
                              {best && (
                                <small className="seo-meta">
                                  {deltaText(rankDelta(best))} · {areaLabel[best.area] ?? best.area}
                                  {rs.length > 1 ? ` 외 ${rs.length - 1}개 영역` : ""}
                                </small>
                              )}
                            </td>
                            <td>
                              {last ? fmtDateTime(last) : "첫 수집 대기"}
                              {err && (
                                <small className="seo-meta">
                                  <Badge tone="amber">{/연결/.test(err) ? "연결 없음" : "수집 실패"}</Badge> {err}
                                  {best ? " · 마지막 성공 결과 유지" : ""}
                                </small>
                              )}
                            </td>
                          </>
                        );
                      })()}
                      <td>
                        <Badge tone={statusLabel[k.status][1]}>{statusLabel[k.status][0]}</Badge>
                      </td>
                      {canWrite && (
                        <td>
                          <div className="seo-actions">
                            {k.status !== "ended" && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                  void run(
                                    patch.mutateAsync({ id: k.id, body: { status: k.status === "tracking" ? "stopped" : "tracking" } }),
                                    k.status === "tracking" ? `'${k.keyword}' 추적을 중지했습니다.` : `'${k.keyword}' 추적을 재개했습니다.`,
                                  )
                                }
                              >
                                {k.status === "tracking" ? "중지" : "재개"}
                              </Button>
                            )}
                            <Button size="sm" variant="secondary" onClick={() => setExtend(k)}>
                              기간 연장
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRemove(k)}>
                              삭제
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Panel>
        </>
      ) : tab === "자산" ? (
        <SeoAssetsPanel key={customerId} me={me} customerId={customerId} />
      ) : (
        <SeoConnections key={customerId} me={me} customerId={customerId} />
      )}
      <ExtendDialog
        target={extend}
        onClose={() => setExtend(null)}
        onSubmit={(days) => {
          const k = extend!;
          setExtend(null);
          void run(patch.mutateAsync({ id: k.id, body: { extendDays: days } }), `'${k.keyword}' 추적을 ${days}일 연장했습니다.`);
        }}
      />
      <Dialog
        open={!!remove}
        onOpenChange={(v) => !v && setRemove(null)}
        title="키워드 삭제"
        description="요청 기록까지 함께 지웁니다. 되돌릴 수 없습니다."
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setRemove(null) }}
            primary={{
              label: "삭제",
              tone: "danger",
              onClick: () => {
                const k = remove!;
                setRemove(null);
                void run(del.mutateAsync(k.id), `'${k.keyword}'를 삭제했습니다.`);
              },
            }}
          />
        }
      >
        <p>{remove?.keyword}</p>
      </Dialog>
    </div>
  );
}

function ExtendDialog({ target, onClose, onSubmit }: { target: SeoKeyword | null; onClose: () => void; onSubmit: (days: number) => void }) {
  const [days, setDays] = useState(30);
  return (
    <Dialog
      open={!!target}
      onOpenChange={(v) => !v && onClose()}
      title="추적 기간 연장"
      description={target ? `${target.keyword} · 현재 종료 ${fmtDate(target.trackingUntil)}` : ""}
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: "연장", onClick: () => onSubmit(days) }} />}
    >
      <Field label="연장 일수" type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} />
    </Dialog>
  );
}

/**
 * 고객 화면 분석 (시안 MarketingAnalytics · seo-analytics 를 실제 데이터로). 고객 웹·데스크톱 공용, 읽기 전용.
 * 순위는 코어 수집 결과(대상 고객사 기준). 키·한도·빌린 키는 보이지 않는다. 수집이 늦으면 "수집 지연 · 마지막 성공 시각"만 안내한다.
 */
